import { useState, useEffect } from "react";
import { Building2, Plus, Search, X, Copy, Check, AlertCircle, TrendingUp, Users, Clock, AlertTriangle } from "lucide-react";
import api, { getErrorMessage } from "../lib/api";

interface Company {
  id: string;
  name: string;
  contact_person?: string;
  contact_email?: string;
  contact_phone?: string;
  notes?: string;
  onboarded_at?: string;
  created_at: string;
  subscription?: {
    id: string;
    plan: string;
    status: string;
    trial_end?: string;
    monthly_price_nok?: number;
    max_projects?: number;
    max_users?: number;
    max_invoices_per_month?: number;
  };
  user_count?: number;
  project_count?: number;
  invoices_this_month?: number;
}

interface Stats {
  total_customers: number;
  active_customers: number;
  trial_customers: number;
  trials_expiring_soon: number;
  past_due: number;
  mrr_nok: number;
}

interface PlanTier {
  name: string;
  monthly_price_nok: number | null;
  max_projects: number | null;
  max_users: number | null;
  max_invoices_per_month: number | null;
  description: string;
}

const PLAN_BADGE: Record<string, string> = {
  starter: "bg-night/[0.06] text-night",
  vekst: "bg-night/[0.06] text-info",
  enterprise: "bg-info/10 text-info",
};

const STATUS_BADGE: Record<string, string> = {
  trial: "bg-flag/10 text-flag",
  active: "bg-pos/10 text-pos",
  past_due: "bg-flag/10 text-flag",
  suspended: "bg-neg/10 text-neg",
  cancelled: "bg-night/[0.06] text-muted",
};

export default function AdminView() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [plans, setPlans] = useState<Record<string, PlanTier>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, s, p] = await Promise.all([
        api.get("/admin/companies"),
        api.get("/admin/stats"),
        api.get("/admin/plans"),
      ]);
      setCompanies(c.data.companies || c.data);
      setStats(s.data);
      setPlans(p.data.plans || p.data);
      setError(null);
    } catch (e: any) {
      setError(getErrorMessage(e, "Kunne ikke hente data"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.contact_email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-night">Admin-panel</h1>
          <p className="text-[13px] text-muted mt-1">Sales-assistert kundeadministrasjon</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-night text-white rounded-xl text-[13px] font-semibold hover:bg-night/90 transition-all shadow-lg shadow-night/20"
        >
          <Plus className="w-4 h-4" /> Ny kunde
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-4 bg-neg/5 border border-neg/20 rounded-xl text-[13px] text-neg">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {/* KPI cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={Users} label="Aktive kunder" value={stats.active_customers} sub={`${stats.total_customers} totalt`} color="brand" />
          <KpiCard icon={TrendingUp} label="MRR" value={`${(stats.mrr_nok / 1000).toFixed(0)}k`} sub="NOK/mnd" color="success" />
          <KpiCard icon={Clock} label="Trials" value={stats.trial_customers} sub={`${stats.trials_expiring_soon} utløper denne uka`} color="amber" />
          <KpiCard icon={AlertTriangle} label="Past due" value={stats.past_due} sub="Krever oppfølging" color="danger" />
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 text-muted absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Søk etter selskap eller e-post..."
          className="w-full pl-10 pr-4 py-3 bg-white border border-line rounded-xl text-[13px] text-night placeholder-muted focus:border-night focus:ring-2 focus:ring-night/10 transition-all"
        />
      </div>

      {/* Companies table */}
      <div className="bg-white border border-line rounded-[14px] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-canvas border-b border-line">
              <tr>
                <th className="text-left px-5 py-3 font-semibold text-muted text-[11px] uppercase tracking-wider">Selskap</th>
                <th className="text-left px-5 py-3 font-semibold text-muted text-[11px] uppercase tracking-wider">Kontakt</th>
                <th className="text-left px-5 py-3 font-semibold text-muted text-[11px] uppercase tracking-wider">Plan</th>
                <th className="text-left px-5 py-3 font-semibold text-muted text-[11px] uppercase tracking-wider">Status</th>
                <th className="text-left px-5 py-3 font-semibold text-muted text-[11px] uppercase tracking-wider">Trial slutt</th>
                <th className="text-left px-5 py-3 font-semibold text-muted text-[11px] uppercase tracking-wider">Opprettet</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {loading ? (
                <tr><td colSpan={6} className="text-center py-12 text-muted">Laster...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12">
                  <Building2 className="w-10 h-10 text-faint mx-auto mb-2" />
                  <p className="text-muted">Ingen kunder enda</p>
                  <p className="text-[12px] text-muted mt-1">Klikk "Ny kunde" for å opprette første konto manuelt</p>
                </td></tr>
              ) : filtered.map(c => (
                <tr key={c.id} onClick={() => setSelectedId(c.id)} className="cursor-pointer hover:bg-night/[0.06] transition-all">
                  <td className="px-5 py-4">
                    <div className="font-semibold text-night">{c.name}</div>
                    {c.contact_person && <div className="text-[11px] text-muted">{c.contact_person}</div>}
                  </td>
                  <td className="px-5 py-4 text-muted">{c.contact_email || "—"}</td>
                  <td className="px-5 py-4">
                    {c.subscription ? (
                      <span className={`px-2 py-1 rounded-md text-[11px] font-semibold ${PLAN_BADGE[c.subscription.plan] || "bg-night/[0.06]"}`}>
                        {plans[c.subscription.plan]?.name || c.subscription.plan}
                      </span>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td className="px-5 py-4">
                    {c.subscription ? (
                      <span className={`px-2 py-1 rounded-md text-[11px] font-semibold ${STATUS_BADGE[c.subscription.status] || ""}`}>
                        {c.subscription.status}
                      </span>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td className="px-5 py-4 text-muted">
                    {c.subscription?.trial_end ? new Date(c.subscription.trial_end).toLocaleDateString("nb-NO") : "—"}
                  </td>
                  <td className="px-5 py-4 text-muted text-[12px]">
                    {new Date(c.created_at).toLocaleDateString("nb-NO")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <CreateCompanyModal
          plans={plans}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchAll(); }}
        />
      )}

      {selectedId && (
        <CompanyDetailModal
          companyId={selectedId}
          plans={plans}
          onClose={() => setSelectedId(null)}
          onChanged={fetchAll}
        />
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color }: any) {
  const colorMap: Record<string, string> = {
    brand: "bg-night/[0.06] text-night",
    success: "bg-pos/10 text-pos",
    amber: "bg-flag/10 text-flag",
    danger: "bg-neg/10 text-neg",
  };
  return (
    <div className="bg-white border border-line rounded-[14px] p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold text-muted uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-night mt-2">{value}</p>
          <p className="text-[11px] text-muted mt-1">{sub}</p>
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorMap[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

function CreateCompanyModal({ plans, onClose, onCreated }: { plans: Record<string, PlanTier>; onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    name: "",
    contact_person: "",
    contact_email: "",
    contact_phone: "",
    admin_email: "",
    admin_full_name: "",
    plan: "starter",
    trial_days: 30,
    notes: "",
    send_welcome_email: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ temp_password: string; admin_email: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.post("/admin/companies", form);
      setResult({ temp_password: r.data.user?.temp_password || r.data.temp_password, admin_email: form.admin_email });
    } catch (e: any) {
      setError(getErrorMessage(e, "Kunne ikke opprette kunde"));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = () => {
    if (!result) return;
    navigator.clipboard.writeText(`E-post: ${result.admin_email}\nPassord: ${result.temp_password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => { if (result) onCreated(); else onClose(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={handleClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-[14px] shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-line flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-night">{result ? "Konto opprettet" : "Ny kunde"}</h2>
          <button onClick={handleClose} className="p-1.5 hover:bg-night/[0.06] rounded-lg"><X className="w-4 h-4" /></button>
        </div>

        {result ? (
          <div className="p-6 space-y-4">
            <div className="p-4 bg-pos/5 border border-pos/20 rounded-xl">
              <p className="text-[13px] text-night">Kontoen er opprettet. Innloggingsdetaljer:</p>
            </div>
            <div className="bg-canvas rounded-xl p-4 space-y-2">
              <div>
                <p className="text-[11px] font-semibold text-muted uppercase">E-post</p>
                <p className="text-[14px] font-mono text-night">{result.admin_email}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold text-muted uppercase">Midlertidig passord</p>
                <p className="text-[14px] font-mono text-night">{result.temp_password}</p>
              </div>
            </div>
            <button onClick={copy} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-night text-white rounded-xl text-[13px] font-semibold hover:bg-night/90">
              {copied ? <><Check className="w-4 h-4" /> Kopiert</> : <><Copy className="w-4 h-4" /> Kopier innloggingsdetaljer</>}
            </button>
            <button onClick={onCreated} className="w-full px-4 py-2.5 border border-line rounded-xl text-[13px] font-medium text-night hover:bg-canvas">Ferdig</button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {error && <div className="p-3 bg-neg/5 border border-neg/20 rounded-xl text-[12px] text-neg">{error}</div>}

            <Field label="Selskapsnavn *" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <Field label="Kontaktperson" value={form.contact_person} onChange={v => setForm({ ...form, contact_person: v })} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Kontakt e-post" value={form.contact_email} onChange={v => setForm({ ...form, contact_email: v })} type="email" />
              <Field label="Telefon" value={form.contact_phone} onChange={v => setForm({ ...form, contact_phone: v })} />
            </div>

            <div className="border-t border-line pt-4">
              <p className="text-[12px] font-semibold text-night mb-3">Admin-bruker</p>
              <Field label="Admin e-post *" value={form.admin_email} onChange={v => setForm({ ...form, admin_email: v })} type="email" />
              <div className="mt-3"><Field label="Fullt navn *" value={form.admin_full_name} onChange={v => setForm({ ...form, admin_full_name: v })} /></div>
            </div>

            <div className="border-t border-line pt-4">
              <p className="text-[12px] font-semibold text-night mb-3">Plan</p>
              <div className="space-y-2">
                {Object.entries(plans).map(([key, plan]) => (
                  <label key={key} className={`block p-3 border rounded-xl cursor-pointer transition-all ${form.plan === key ? "border-night bg-night/[0.06]" : "border-line hover:border-line"}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <input type="radio" checked={form.plan === key} onChange={() => setForm({ ...form, plan: key })} className="accent-night" />
                        <div>
                          <p className="text-[13px] font-semibold text-night">{plan.name}</p>
                          <p className="text-[11px] text-muted">{plan.description}</p>
                        </div>
                      </div>
                      <p className="text-[13px] font-bold text-night shrink-0 ml-3">
                        {plan.monthly_price_nok ? `${(plan.monthly_price_nok / 1000).toFixed(0)}k` : "Avtalt"}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <Field label="Trial-lengde (dager)" value={String(form.trial_days)} onChange={v => setForm({ ...form, trial_days: parseInt(v) || 30 })} type="number" />

            <div>
              <label className="text-[12px] font-semibold text-night block mb-1.5">Sales-notater</label>
              <textarea
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-line rounded-xl text-[13px] focus:border-night focus:ring-2 focus:ring-night/10"
              />
            </div>

            <label className="flex items-center gap-2 text-[12px] text-night">
              <input type="checkbox" checked={form.send_welcome_email} onChange={e => setForm({ ...form, send_welcome_email: e.target.checked })} className="accent-night" />
              Send velkomst-e-post automatisk
            </label>

            <div className="flex gap-2 pt-2">
              <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-line rounded-xl text-[13px] font-medium hover:bg-canvas">Avbryt</button>
              <button
                onClick={submit}
                disabled={submitting || !form.name || !form.admin_email || !form.admin_full_name}
                className="flex-1 px-4 py-2.5 bg-night text-white rounded-xl text-[13px] font-semibold hover:bg-night/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Oppretter..." : "Opprett kunde"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CompanyDetailModal({ companyId, plans, onClose, onChanged }: { companyId: string; plans: Record<string, PlanTier>; onClose: () => void; onChanged: () => void }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [extendDays, setExtendDays] = useState(30);
  const [busy, setBusy] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/companies/${companyId}`);
      setData(r.data);
      setNotes(r.data.notes || "");
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [companyId]);

  const action = async (fn: () => Promise<any>, msg?: string) => {
    if (msg && !window.confirm(msg)) return;
    setBusy(true);
    try { await fn(); await fetchData(); onChanged(); } catch (e: any) {
      alert(getErrorMessage(e, "Feil"));
    } finally { setBusy(false); }
  };

  const saveNotes = () => action(() => api.patch(`/admin/companies/${companyId}`, { notes }));
  const updateContactField = (field: string, value: string) => action(() => api.patch(`/admin/companies/${companyId}`, { [field]: value }));
  const suspend = () => action(() => api.post(`/admin/companies/${companyId}/suspend`), "Suspendere denne kunden?");
  const activate = () => action(() => api.post(`/admin/companies/${companyId}/activate`));
  const extendTrial = () => action(() => api.patch(`/admin/subscriptions/${data.subscription.id}`, { extend_trial_days: extendDays }));
  const changePlan = (plan: string) => action(() => api.patch(`/admin/subscriptions/${data.subscription.id}`, { plan }));
  const cancel = () => action(() => api.post(`/admin/subscriptions/${data.subscription.id}/cancel`), "Kansellere abonnementet?");
  const deleteCompany = () => action(async () => {
    await api.delete(`/admin/companies/${companyId}`);
    onClose();
  }, "Slette dette selskapet permanent (soft delete)?");

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-[14px] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-line flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-night">{loading ? "Laster..." : data?.name}</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-night/[0.06] rounded-lg"><X className="w-4 h-4" /></button>
        </div>

        {data && (
          <div className="p-6 space-y-5">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <StatBox label="Brukere" value={data.user_count || 0} />
              <StatBox label="Prosjekter" value={data.project_count || 0} />
              <StatBox label="Fakturaer/mnd" value={data.invoices_this_month || 0} />
            </div>

            {/* Kontakt — redigerbart */}
            <Section title="Kontaktinformasjon">
              <EditableKV k="Kontaktperson" v={data.contact_person}
                onSave={v => updateContactField("contact_person", v)} />
              <EditableKV k="E-post" v={data.contact_email}
                onSave={v => updateContactField("contact_email", v)} />
              <EditableKV k="Telefon" v={data.contact_phone}
                onSave={v => updateContactField("contact_phone", v)} />
              <KV k="Opprettet" v={new Date(data.created_at).toLocaleDateString("nb-NO")} />
            </Section>

            {/* Subscription */}
            {data.subscription && (
              <Section title="Abonnement">
                <KV k="Plan" v={plans[data.subscription.plan]?.name || data.subscription.plan} />
                <KV k="Status" v={data.subscription.status} />
                <KV k="Pris/mnd" v={data.subscription.monthly_price_nok ? `${data.subscription.monthly_price_nok.toLocaleString("nb-NO")} NOK` : "—"} />
                <KV k="Trial slutt" v={data.subscription.trial_end ? new Date(data.subscription.trial_end).toLocaleDateString("nb-NO") : "—"} />
                <KV k="Max prosjekter" v={data.subscription.max_projects ?? "Ubegrenset"} />
                <KV k="Max brukere" v={data.subscription.max_users ?? "Ubegrenset"} />

                <div className="pt-3 border-t border-line mt-3 space-y-2">
                  <div className="flex gap-2">
                    <input type="number" value={extendDays} onChange={e => setExtendDays(parseInt(e.target.value) || 0)}
                      className="w-20 px-2 py-1.5 border border-line rounded-lg text-[12px]" />
                    <button onClick={extendTrial} disabled={busy} className="px-3 py-1.5 bg-night/[0.06] text-info rounded-lg text-[12px] font-semibold hover:bg-night/[0.1]">
                      Forleng trial
                    </button>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {Object.keys(plans).map(p => (
                      <button key={p} onClick={() => changePlan(p)} disabled={busy || data.subscription.plan === p}
                        className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold ${data.subscription.plan === p ? "bg-night/[0.06] text-faint cursor-not-allowed" : "bg-canvas text-night hover:bg-night/[0.06]"}`}>
                        → {plans[p].name}
                      </button>
                    ))}
                  </div>
                </div>
              </Section>
            )}

            {/* Notes */}
            <div>
              <label className="text-[12px] font-semibold text-night block mb-1.5">Sales-notater</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                className="w-full px-3 py-2 border border-line rounded-xl text-[13px]" />
              <button onClick={saveNotes} disabled={busy} className="mt-2 px-3 py-1.5 bg-night text-white rounded-lg text-[12px] font-semibold hover:bg-night/90">
                Lagre notater
              </button>
            </div>

            {/* Danger zone */}
            <div className="border-t border-line pt-4 space-y-2">
              <p className="text-[11px] font-semibold text-muted uppercase">Handlinger</p>
              <div className="flex gap-2 flex-wrap">
                {data.subscription?.status === "suspended" ? (
                  <button onClick={activate} disabled={busy} className="px-3 py-2 bg-pos/10 text-pos rounded-lg text-[12px] font-semibold hover:bg-pos/20">
                    Reaktiver
                  </button>
                ) : (
                  <button onClick={suspend} disabled={busy} className="px-3 py-2 bg-flag/10 text-flag rounded-lg text-[12px] font-semibold hover:bg-flag/20">
                    Suspendér
                  </button>
                )}
                {data.subscription && (
                  <button onClick={cancel} disabled={busy} className="px-3 py-2 bg-night/[0.06] text-night rounded-lg text-[12px] font-semibold hover:bg-night/[0.1]">
                    Kanseller abonnement
                  </button>
                )}
                <button onClick={deleteCompany} disabled={busy} className="px-3 py-2 bg-neg/10 text-neg rounded-lg text-[12px] font-semibold hover:bg-neg/20">
                  Slett selskap
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <div>
      <label className="text-[12px] font-semibold text-night block mb-1.5">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-line rounded-xl text-[13px] focus:border-night focus:ring-2 focus:ring-night/10" />
    </div>
  );
}

function Section({ title, children }: { title: string; children: any }) {
  return (
    <div className="bg-canvas rounded-xl p-4">
      <p className="text-[11px] font-semibold text-muted uppercase mb-2">{title}</p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function KV({ k, v }: { k: string; v: any }) {
  return (
    <div className="flex justify-between text-[12px]">
      <span className="text-muted">{k}</span>
      <span className="text-night font-medium">{v || "—"}</span>
    </div>
  );
}

function EditableKV({ k, v, onSave }: { k: string; v: any; onSave: (val: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(v || "");
  return (
    <div className="flex justify-between items-center text-[12px] gap-2">
      <span className="text-muted shrink-0">{k}</span>
      {editing ? (
        <div className="flex items-center gap-1">
          <input value={val} onChange={e => setVal(e.target.value)} autoFocus
            className="px-2 py-1 border border-line rounded-lg text-[12px] text-night w-48"
            onKeyDown={e => { if (e.key === "Enter") { onSave(val); setEditing(false); } if (e.key === "Escape") setEditing(false); }} />
          <button onClick={() => { onSave(val); setEditing(false); }} className="text-[11px] font-semibold text-info hover:underline">Lagre</button>
        </div>
      ) : (
        <button onClick={() => setEditing(true)} className="text-night font-medium hover:text-info transition-colors text-right">
          {v || "—"}
        </button>
      )}
    </div>
  );
}

function StatBox({ label, value }: { label: string; value: any }) {
  return (
    <div className="bg-canvas rounded-xl p-3 text-center">
      <p className="text-xl font-bold text-night">{value}</p>
      <p className="text-[10px] text-muted uppercase mt-0.5">{label}</p>
    </div>
  );
}
